import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, Users, Calendar, IndianRupee, Activity, MapPin, ExternalLink } from 'lucide-react';

export default function ArenaxSuperAdmin() {
  const [overview, setOverview] = useState(null);
  const [venues, setVenues] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    api.get('/arenax/admin/overview').then(r => setOverview(r.data)).catch(() => {});
    api.get('/arenax/admin/venues').then(r => setVenues(r.data.venues || [])).catch(() => {});
    api.get('/arenax/admin/bookings').then(r => setBookings(r.data.bookings || [])).catch(() => {});
    api.get('/arenax/admin/players').then(r => setPlayers(r.data.players || [])).catch(() => {});
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="arenax-super-admin">
      <div className="flex items-center justify-between">
        <div>
          <Badge style={{ background: 'var(--ax-primary-gradient)', color: '#020617' }}>ARENAX · COMMAND CENTER</Badge>
          <h1 className="text-2xl font-black mt-2 flex items-center gap-2">
            <Trophy className="h-6 w-6 text-cyan-600" />ArenaX Operations
          </h1>
          <p className="text-sm text-muted-foreground">Cross-tenant visibility into India's pay & play network.</p>
        </div>
      </div>

      {/* KPIs */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard icon={MapPin} label="Active Venues" value={`${overview.venues.active}/${overview.venues.total}`} color="#06b6d4" />
          <KpiCard icon={Users} label="Players" value={overview.players} color="#f59e0b" />
          <KpiCard icon={Calendar} label="Bookings (paid)" value={`${overview.bookings.paid}/${overview.bookings.total}`} color="#ec4899" />
          <KpiCard icon={IndianRupee} label="Gross GMV" value={`₹${(overview.revenue?.gross || 0).toFixed(0)}`} color="#10b981" />
          <KpiCard icon={Activity} label="Platform Commission" value={`₹${(overview.revenue?.platform_commission || 0).toFixed(0)}`} color="#a855f7" />
        </div>
      )}

      <Tabs defaultValue="venues">
        <TabsList>
          <TabsTrigger value="venues" data-testid="arenax-admin-tab-venues">Venues ({venues.length})</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="arenax-admin-tab-bookings">Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger value="players" data-testid="arenax-admin-tab-players">Players ({players.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="venues">
          <Card>
            <CardContent className="p-0">
              {venues.length === 0 ? (
                <div className="text-center py-12 text-sm text-muted-foreground">No venues onboarded yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                      <th className="p-3">Venue</th><th className="p-3">City</th><th className="p-3">Sports</th><th className="p-3">Status</th><th className="p-3">Public</th>
                    </tr>
                  </thead>
                  <tbody>
                    {venues.map(v => (
                      <tr key={v.id} className="border-b hover:bg-muted/30" data-testid={`arenax-admin-venue-${v.id}`}>
                        <td className="p-3 font-semibold">{v.name}</td>
                        <td className="p-3 text-muted-foreground">{v.city || '—'}</td>
                        <td className="p-3"><div className="flex flex-wrap gap-1">{(v.sports_supported || []).slice(0, 3).map(s => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}</div></td>
                        <td className="p-3"><Badge variant={v.is_active ? 'default' : 'outline'}>{v.is_active ? 'Active' : 'Paused'}</Badge></td>
                        <td className="p-3"><a href={`/arenax/venues/${v.slug || v.id}`} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline text-xs flex items-center gap-1">/{v.slug} <ExternalLink className="h-3 w-3" /></a></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bookings">
          <Card>
            <CardContent className="p-0">
              {bookings.length === 0 ? (
                <div className="text-center py-12 text-sm text-muted-foreground">No bookings yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                      <th className="p-3">Code</th><th className="p-3">Player</th><th className="p-3">Sport</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map(b => (
                      <tr key={b.id} className="border-b hover:bg-muted/30" data-testid={`arenax-admin-booking-${b.id}`}>
                        <td className="p-3 font-mono font-semibold">{b.booking_code}</td>
                        <td className="p-3">{b.player_name}</td>
                        <td className="p-3 text-muted-foreground">{b.sport}</td>
                        <td className="p-3 font-bold">₹{b.total_amount}</td>
                        <td className="p-3"><Badge variant={b.status === 'confirmed' ? 'default' : 'secondary'}>{b.status?.replace('_', ' ')}</Badge></td>
                        <td className="p-3 text-xs text-muted-foreground">{(b.created_at || '').slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="players">
          <Card>
            <CardContent className="p-0">
              {players.length === 0 ? (
                <div className="text-center py-12 text-sm text-muted-foreground">No players yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                      <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Auth</th><th className="p-3">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.map(p => (
                      <tr key={p.id} className="border-b hover:bg-muted/30" data-testid={`arenax-admin-player-${p.id}`}>
                        <td className="p-3 font-semibold">{p.name}</td>
                        <td className="p-3 text-muted-foreground">{p.email}</td>
                        <td className="p-3"><Badge variant="outline" className="text-[10px]">{p.auth_provider || 'email'}</Badge></td>
                        <td className="p-3 text-xs text-muted-foreground">{(p.created_at || '').slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="h-3.5 w-3.5" style={{ color }} />
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
        </div>
        <div className="text-2xl font-black" style={{ color }}>{value}</div>
      </CardContent>
    </Card>
  );
}
